import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpEventType } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface SolicitudData {
  colaboradorId: number;
  esGestante: boolean;
  archivosPdf: File[];
  archivosXml: File[];
  metadatos?: any[];
}

export interface UploadProgress {
  loaded: number;
  total: number;
  percentage: number;
}

@Injectable({
  providedIn: 'root'
})
export class SolicitudService {
  private apiUrl = environment.api;

  constructor(private http: HttpClient) {}

  crearSolicitudReembolso(
    colaboradorId: number, 
    solicitudData: SolicitudData
  ): Observable<any> {
    const formData = new FormData();

    formData.append('esGestante', solicitudData.esGestante.toString());

    solicitudData.archivosPdf.forEach((file, index) => {
      formData.append('archivos_pdf', file, file.name);
    });

    solicitudData.archivosXml.forEach((file, index) => {
      formData.append('archivos_xml', file, file.name);
    });

    if (solicitudData.metadatos && solicitudData.metadatos.length > 0) {
      formData.append('metadatos_archivos', JSON.stringify(solicitudData.metadatos));
    }

    const headers = new HttpHeaders();

    return this.http.post(
      `${this.apiUrl}/colaborador/${colaboradorId}/solicitud-reembolso`,
      formData,
      { 
        headers,
        reportProgress: true,
        observe: 'events'
      }
    );
  }

  crearSolicitudConProgreso(
    colaboradorId: number, 
    solicitudData: SolicitudData
  ): Observable<any> {
    const formData = this.buildFormData(solicitudData);

    return this.http.post(
      `${this.apiUrl}/colaborador/${colaboradorId}/solicitud-reembolso`,
      formData,
      { 
        reportProgress: true,
        observe: 'events'
      }
    ).pipe(
      map(event => {
        switch (event.type) {
          case HttpEventType.UploadProgress:
            const progress: UploadProgress = {
              loaded: event.loaded,
              total: event.total || 0,
              percentage: event.total ? Math.round(100 * event.loaded / event.total) : 0
            };
            return { type: 'progress', progress };
          
          case HttpEventType.Response:
            return { type: 'response', body: event.body };
          
          default:
            return { type: 'other', event };
        }
      })
    );
  }

  crearSolicitudPares(
    solicitudData: SolicitudData,
    paresArchivos: Array<{id: string, archivoPdf: File | null, archivoXml: File | null, completado: boolean}>,
    esGestante: boolean,
    colaboradorId: number
  ): Observable<any> {
    const formData = this.buildFormDataPares(solicitudData, paresArchivos,esGestante, colaboradorId);
    return this.http.post(
      `${this.apiUrl}Colaborador/solicitud-reembolso`,
      formData
    )
  }


  validarArchivos(archivosPdf: File[], archivosXml: File[]): string[] {
    const errores: string[] = [];

    if (archivosPdf.length === 0) {
      errores.push('Debe seleccionar al menos un archivo PDF');
    }

    if (archivosXml.length === 0) {
      errores.push('Debe seleccionar al menos un archivo XML');
    }

    archivosPdf.forEach((file, index) => {
      if (file.type !== 'application/pdf') {
        errores.push(`El archivo ${file.name} no es un PDF válido`);
      }
      if (file.size > 10 * 1024 * 1024) {
        errores.push(`El archivo PDF ${file.name} excede el tamaño máximo de 10MB`);
      }
    });

    archivosXml.forEach((file, index) => {
      if (!file.type.includes('xml') && !file.name.toLowerCase().endsWith('.xml')) {
        errores.push(`El archivo ${file.name} no es un XML válido`);
      }
      if (file.size > 1 * 1024 * 1024) {
        errores.push(`El archivo XML ${file.name} excede el tamaño máximo de 1MB`);
      }
    });

    if (archivosPdf.length > 10) {
      errores.push('No puede enviar más de 10 archivos PDF');
    }

    if (archivosXml.length > 10) {
      errores.push('No puede enviar más de 10 archivos XML');
    }

    return errores;
  }


  validarPares(paresArchivos: Array<{archivoPdf: File | null, archivoXml: File | null}>): string[] {
    const errores: string[] = [];

    if (paresArchivos.length === 0) {
      errores.push('Debe agregar al menos un documento');
      return errores;
    }

    const paresCompletos = paresArchivos.filter(par => par.archivoPdf && par.archivoXml);
    if (paresCompletos.length === 0) {
      errores.push('Debe completar al menos un par de archivos (PDF + XML)');
    }

    paresArchivos.forEach((par, index) => {
      if (par.archivoPdf) {
        if (par.archivoPdf.type !== 'application/pdf') {
          errores.push(`Documento ${index + 1}: El archivo PDF no es válido`);
        }
        if (par.archivoPdf.size > 10 * 1024 * 1024) {
          errores.push(`Documento ${index + 1}: El PDF excede 10MB`);
        }
      }

      if (par.archivoXml) {
        if (!par.archivoXml.type.includes('xml') && !par.archivoXml.name.toLowerCase().endsWith('.xml')) {
          errores.push(`Documento ${index + 1}: El archivo XML no es válido`);
        }
        if (par.archivoXml.size > 1 * 1024 * 1024) {
          errores.push(`Documento ${index + 1}: El XML excede 1MB`);
        }
      }

      if ((par.archivoPdf && !par.archivoXml) || (!par.archivoPdf && par.archivoXml)) {
        errores.push(`Documento ${index + 1}: Debe incluir tanto PDF como XML`);
      }
    });

    return errores;
  }


  private buildFormData(solicitudData: SolicitudData): FormData {
    const formData = new FormData();

    formData.append('esGestante', solicitudData.esGestante.toString());

    solicitudData.archivosPdf.forEach((file, index) => {
      formData.append('archivos_pdf', file, file.name);
      formData.append(`pdf_orden_${index}`, index.toString());
    });

    solicitudData.archivosXml.forEach((file, index) => {
      formData.append('archivos_xml', file, file.name);
      formData.append(`xml_orden_${index}`, index.toString());
    });

    formData.append('total_pares', Math.min(solicitudData.archivosPdf.length, solicitudData.archivosXml.length).toString());

    if (solicitudData.metadatos && solicitudData.metadatos.length > 0) {
      formData.append('metadatos_archivos', JSON.stringify(solicitudData.metadatos));
    }

    return formData;
  }

  buildFormDataPares(
    solicitudData: SolicitudData, 
    paresArchivos: Array<{id: string, archivoPdf: File | null, archivoXml: File | null, completado: boolean}>,
    esGestante: boolean,
    colaboradorId: number
  ): FormData {
    try {
         const formData = new FormData();
        formData.append('esGestante', (esGestante?.toString()) || 'false');
        formData.append('ColaboradorId', colaboradorId?.toString());

        const paresCompletos = paresArchivos.filter(par => par.completado && par.archivoPdf && par.archivoXml);    
        paresCompletos.forEach((par) => {
          if (par.archivoPdf) {
            formData.append('pdf', par.archivoPdf, par.archivoPdf.name);
          }
        });

        paresCompletos.forEach((par) => {
          if (par.archivoXml) {
            formData.append('xml', par.archivoXml, par.archivoXml.name);
          }
        });

        const metadatos: Array<{nombre: string, tamanio: number, tipo: string, fecha: string, index: number}> = [];
        
        paresCompletos.forEach((par, index) => {
          if (par.archivoPdf) {
            metadatos.push({
              nombre: par.archivoPdf.name,
              tamanio: par.archivoPdf.size,
              tipo: 'factura_pdf',
              fecha: new Date().toISOString(),
              index: index
            });
          }
          
          if (par.archivoXml) {
            metadatos.push({
              nombre: par.archivoXml.name,
              tamanio: par.archivoXml.size,
              tipo: 'xml_factura',
              fecha: new Date().toISOString(),
              index: index
            });
          }
        });


        formData.append('total_pares', paresCompletos.length.toString());

        return formData; 
    } catch (error) {
      console.error('Error al construir FormData para pares de archivos:', error);
      throw error;
    }
  }


  generarMetadatos(archivosPdf: File[], archivosXml: File[]): any[] {
    const metadatos: any[] = [];

    archivosPdf.forEach((file, index) => {
      metadatos.push({
        nombre: file.name,
        tamaño: file.size,
        tipo: 'factura_pdf',
        fecha: new Date().toISOString(),
        index: index
      });
    });

    archivosXml.forEach((file, index) => {
      metadatos.push({
        nombre: file.name,
        tamaño: file.size,
        tipo: 'xml_factura',
        fecha: new Date().toISOString(),
        index: index
      });
    });

    return metadatos;
  }

  /**
   * Generar metadatos para pares de archivos
   */
  generarMetadatosPares(paresCompletos: Array<{id: string, archivoPdf: File, archivoXml: File}>): any[] {
    const metadatos: any[] = [];

    paresCompletos.forEach((par, index) => {
      metadatos.push({
        par_id: par.id,
        indice_match: index,
        pdf_info: {
          nombre: par.archivoPdf.name,
          tamaño: par.archivoPdf.size,
          tipo: 'application/pdf',
          fecha: new Date().toISOString(),
          indice_pdf: index
        },
        xml_info: {
          nombre: par.archivoXml.name,
          tamaño: par.archivoXml.size,
          tipo: 'xml',
          fecha: new Date().toISOString(),
          indice_xml: index
        },
        relacion: 'factura_comprobante',
        fecha_par: new Date().toISOString()
      });
    });

    return metadatos;
  }
}
